// 라바 라군 방 전체: 장면 + 낮밤 + 간헐천 + 규칙 + 카피바라 29마리 + 내 카피바라.
// 처음 들어갈 때 만든다 (모바일 첫 로딩을 가볍게). 프로토타입 buildLagoon 2142–2520, lagoonTick 2704–2735, tick 3973–3977.
import * as THREE from 'three';
import { MENUS } from '@capy/shared';
import type { Stage } from '../../engine/renderer';
import type { Lights } from '../../engine/lights';
import type { Capy } from '../../capy/agent';
import { pose } from '../../capy/pose';
import { tickPops, updateTosses } from '../../food/eating';
import { tickSteam } from '../quiet/baths';
import { GEY, SPAWN } from './layout';
import { buildLagoonScene, type LagoonScene } from './scene';
import { createDayNight, type DayNight } from './sky';
import { createGeyser, type GeyserKind, type GeyserState } from './geyser';
import { asLagoon, barProximity, lagoonStep, lagoonThink, populate, type BarJob, type LagoonCapy, type LagoonRules } from './bots';

export interface LagoonHooks {
  /** NPC나 내 카피바라가 말할 때 (말풍선). */
  say(a: Capy, text: string, t: number): void;
  /** 안내 문구 (간헐천·오로라·바·헹굼). */
  note?(key: LagoonNote): void;
  /** 내 카피바라가 바에 도착했다 (메뉴 열기). */
  onBar?(job: BarJob): void;
}
export type LagoonNote = 'clean' | 'aurora' | 'rinse' | 'rinseMask' | `warn.${GeyserKind}` | `fire.${GeyserKind}`;

export interface LagoonRoom {
  ls: LagoonScene;
  world: THREE.Group;
  R: LagoonRules;
  day: DayNight;
  geyser: GeyserState;
  player: LagoonCapy;
  /** 방에 들어올 때: 내 카피바라를 라군에 두고 자리를 정한다. */
  enter(): void;
  update(t: number, dt: number): void;
}

const isMask = (k: string | null | undefined) => !!k && k in MENUS.maskbar;

export function createLagoonRoom(scene: THREE.Object3D, stage: Stage, lights: Lights, player: Capy, hooks: LagoonHooks): LagoonRoom {
  const ls = buildLagoonScene(scene);
  const world = ls.world as THREE.Group;
  const me = asLagoon(player);
  // 프로토타입 setTimeout 대신 시계로 미룬 일 (간헐천·오로라에 반응하는 말). 같은 순서로 Math.random을 쓴다.
  const later: { at: number; fn: (t: number) => void }[] = [];
  let now = 0;
  const R: LagoonRules = {
    W: ls.W, agents: [me], world, rinse: ls.rinse, rinseUser: null, guard: { mask: false, slush: false, rinse: false }, barOpen: null,
    say: (a, text) => hooks.say(a, text, now),
    onBar: (job) => hooks.onBar?.(job),
    onRinse: (masked) => hooks.note?.(masked ? 'rinseMask' : 'rinse'),
  };
  populate(R);
  const day = createDayNight(ls, stage, lights, {
    onAurora() {
      R.agents.forEach((o) => { if (!o.isPlayer && Math.random() < 0.3) { const at = now + Math.random() * 3; later.push({ at, fn: () => R.say(o, Math.random() < 0.5 ? 'ayy' : 'my my') }); } });
      hooks.note?.('aurora');
    },
  });
  const geyser = createGeyser(ls.geyser, {
    onWarn(kind) { if (kind !== 'small') hooks.note?.(`warn.${kind}`); },
    onFire(kind, t) {
      hooks.note?.(`fire.${kind}`);
      const share = kind === 'big' ? 0.5 : kind === 'medium' ? 0.25 : 0.06;
      R.agents.forEach((o) => {
        if (o.isPlayer || Math.random() > share) return;
        later.push({ at: t + Math.random() * 1.5, fn: (t2) => {
          R.say(o, kind === 'big' ? (Math.random() < 0.5 ? 'yay yay' : 'ayy ayy') : kind === 'medium' ? 'yay' : 'mmm');
          if (kind !== 'small') { o.emote = 'wave'; o.emoteStart = t2; }
          o.targetFacing = Math.atan2(GEY.x - o.pos.x, GEY.z - o.pos.z);
        } });
      });
    },
  });
  const ctx = { room: 'lagoon' as const };
  return {
    ls, world, R, day, geyser, player: me,
    enter() {
      world.add(me.c.root); if (me.ripple) world.add(me.ripple);
      me.pos.set(SPAWN.x, 0, SPAWN.z); me.c.root.position.copy(me.pos); me.facing = me.targetFacing = 0; me.path = null;
      me.mode = 'idle'; me.wander = null; me.onArrive = null; me.washT = 0; me.bathAmt = 0; R.rinseUser = null; R.barOpen = null;
    },
    update(t, dt) {
      now = t;
      for (const a of R.agents) {
        lagoonThink(R, a, t);
        const masked = a.isPlayer && !!a.washT && isMask(a.faceKey);
        pose(a, t, dt, lagoonStep(R, a, t, dt), ctx);
        if (R.rinseUser === a && !a.washT) R.rinseUser = null;
        if (masked && !isMask(a.faceKey)) hooks.note?.('clean');
      }
      for (let i = later.length - 1; i >= 0; i--) if (t >= later[i]!.at) { const job = later[i]!; later.splice(i, 1); job.fn(t); }
      day.rinseUser = R.rinseUser; day.tick(t, dt);
      geyser.tick(t);
      updateTosses(t);
      tickSteam(ls.steam, t);
      tickPops(t);
      barProximity(R, me, t);
    },
  };
}
